import apiClient from "../lib/api/client";

export type ParameterStatus = "active" | "inactive";

export interface ParameterItem {
  id: number;
  name: string;
  value: string;
  description: string;
  status: ParameterStatus;
}

export interface ParametersPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

export interface ParametersListResponse {
  message: string;
  data: {
    data: ParameterItem[];
    pagination: ParametersPagination;
  };
}

export interface ParametersListNormalizedResponse {
  message: string;
  data: ParameterItem[];
  pagination: ParametersPagination;
}

export interface ParametersListParams {
  page?: number;
  per_page?: number;
}

export interface CreateParameterBody {
  name: string;
  value: string;
  description: string;
  status: ParameterStatus;
}

export interface UpdateParameterBody extends Partial<CreateParameterBody> {}

const defaultPagination: ParametersPagination = {
  current_page: 1,
  per_page: 15,
  total: 0,
  last_page: 1,
  from: 0,
  to: 0,
};

function buildQuery(params: ParametersListParams): string {
  const sp = new URLSearchParams();
  if (params.page != null) sp.set("page", String(params.page));
  if (params.per_page != null) sp.set("per_page", String(params.per_page));
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

function normalizeListResponse(raw: unknown): ParametersListNormalizedResponse {
  if (!raw || typeof raw !== "object") {
    return { message: "", data: [], pagination: defaultPagination };
  }

  const r = raw as Record<string, any>;
  const message = typeof r.message === "string" ? r.message : "";

  // Envelope esperado: { message, data: { data: [...], pagination: {...} } }
  const nested = r.data;
  const list = nested?.data ?? [];
  const pagination = nested?.pagination ?? defaultPagination;

  return {
    message,
    data: Array.isArray(list) ? (list as ParameterItem[]) : [],
    pagination: pagination && typeof pagination === "object" ? pagination : defaultPagination,
  };
}

export interface ParameterSingleNormalizedResponse {
  message: string;
  data: ParameterItem;
}

function normalizeSingleResponse(raw: unknown): ParameterSingleNormalizedResponse {
  if (!raw || typeof raw !== "object") {
    return { message: "", data: null as any };
  }
  const r = raw as Record<string, any>;
  const message = typeof r.message === "string" ? r.message : "";

  // Envelope comum: { message, data: { ...parameterFields } }
  const dataNode = r.data ?? r;
  const data = dataNode as ParameterItem;
  return { message, data };
}

export const parametersService = {
  async getList(params: ParametersListParams = {}): Promise<ParametersListNormalizedResponse> {
    const query = buildQuery(params);
    const response = await apiClient.get<unknown>(`/parameters${query}`);
    return normalizeListResponse(response);
  },

  async create(body: CreateParameterBody): Promise<ParameterItem> {
    const response = await apiClient.post<unknown>(`/parameters`, body);
    const normalized = normalizeSingleResponse(response);
    return normalized.data;
  },

  async getById(id: number): Promise<ParameterItem> {
    const response = await apiClient.get<unknown>(`/parameters/${id}`);
    const normalized = normalizeSingleResponse(response);
    return normalized.data;
  },

  async update(id: number, body: UpdateParameterBody): Promise<ParameterItem> {
    const response = await apiClient.put<unknown>(`/parameters/${id}`, body);
    const normalized = normalizeSingleResponse(response);
    return normalized.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/parameters/${id}`);
  },
};

export default parametersService;

